/**
 * Render-path preparation of the operator-maintained legal document HTML.
 *
 * The stored text values stay untouched and lossless (they are exported/imported through the language
 * file and pinned by the backend `LegalDocumentSeedFunctionalTest`) — everything in this module happens
 * on the way into the DOM only.
 */

/**
 * Class name of the container the prepared HTML is written into. Every selector of the document's own
 * `<style>` rules is prefixed with this class, so a rule cannot match header, footer or any other
 * element outside the container.
 *
 * Known limits of that scoping — it constrains *element matching*, it is not a sandbox: page-level
 * at-rules without a selector (`@page`, `@font-face`, `@property`) are passed through unchanged, and a
 * rule that stays inside the container can still cover the viewport visually (`position: fixed`,
 * `z-index`). The remaining boundary against a malicious document is the CSP plus the fact that only
 * operators can edit these texts.
 */
export const LEGAL_DOCUMENT_SCOPE_CLASS = "legal-document-content";

/**
 * Prefix put in front of every selector. `:where()` keeps the prefix at zero specificity, so the
 * document's own rules win against each other exactly as they did before scoping.
 */
const SCOPE_PREFIX = `:where(.${LEGAL_DOCUMENT_SCOPE_CLASS})`;

/**
 * Prepares a legal document's HTML for insertion into the content container.
 *
 * Two transformations, both required because HTML fragment parsing keeps `<meta>`, `<style>`, `<link>`
 * and `<base>` while only the document shell (`<!DOCTYPE>`, `<html>`, `<head>`, `<body>`) is discarded:
 *
 * 1. `<meta http-equiv="...">` elements are removed. A `refresh` value would navigate the whole page
 *    when inserted — an open redirect that needs no script execution and that no CSP directive covers
 *    (`base-uri`, which handles the comparable `<base href>` case, has no counterpart for meta refresh).
 * 2. Selectors inside `<style>` blocks are scoped to the content container, so a rule for `h1`/`h2`/`h3`
 *    (as used by the terms and conditions) no longer styles the surrounding application chrome.
 *
 * The result is returned as a live `DocumentFragment`, NOT as an HTML string, and the caller inserts it
 * with `replaceChildren`. This is a security property, not a style preference: serializing the filtered
 * fragment back to HTML and re-parsing it (`innerHTML`) gives a prepared `<style>` text a second chance
 * to break out of its element: a `</style>` that is split by a CSS comment survives the first parse as
 * plain style text (so the `<meta http-equiv="refresh">` behind it is text, not an element, while the
 * filter runs), but after serialize + re-parse it becomes a real closing tag and the meta a live element
 * behind the filter. With a single parse and a live fragment, text inside `<style>` is only ever read as
 * CSS and never re-enters the HTML parser. The exact payload is pinned in the unit test.
 *
 * @param html - The document's raw HTML as maintained in the text store
 * @returns A fragment holding the prepared nodes, to be inserted via `replaceChildren`
 */
export function prepareLegalDocumentHtml(html: string): DocumentFragment {
    const template = document.createElement("template");
    // Fragment parsing, identical to what the assignment to the container's innerHTML would do:
    // the document shell is dropped, meta/style/link survive.
    template.innerHTML = html;

    template.content.querySelectorAll("meta[http-equiv]").forEach((meta) => meta.remove());
    template.content.querySelectorAll("style").forEach((style) => {
        const scoped = scopeCss(style.textContent ?? "");
        if (scoped === null) {
            // The CSSOM is the only mechanism that scopes reliably; without it the document keeps its
            // text but loses its styling — an unscoped stylesheet must never reach the page.
            style.remove();
        } else {
            style.textContent = scoped;
        }
    });

    return template.content;
}

/** Selectors that address the document root in a standalone document — the container takes their role. */
const ROOT_SELECTOR_HEAD = /^(?:html|body|:root)(?:\s*[>+~]\s*|\s+|$)/i;

/** Selector used to check whether a candidate selector is parseable; never matches any element. */
const PROBE_SELECTOR = "#legal-document-scope-probe";

/**
 * Rewrites every selector of one `<style>` element so it only matches inside the scope container.
 *
 * The rewriting runs on the CSSOM, not on the CSS text: the browser's own parser produces the rule
 * tree, and only `CSSStyleRule.selectorText` is reassigned. A text-level tokenizer cannot do this
 * safely — braces inside declaration values (`content: "}"`), commas inside `:not(...)` or attribute
 * values, and at-statements without a block (`@charset`, `@import`) each make brace/comma counting
 * pick the wrong boundary, and every such miss leaves a rule unscoped.
 *
 * The sheet is built with the `CSSStyleSheet` constructor, which parses without attaching anything to
 * the document — no rule of the untrusted stylesheet is ever live, and `@import` is not fetched.
 *
 * @param css - The stylesheet text of one `<style>` element
 * @returns The scoped stylesheet text, or `null` when the CSSOM is unavailable (see caller)
 */
function scopeCss(css: string): string | null {
    let sheet: CSSStyleSheet;
    let probe: CSSStyleRule;
    try {
        sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        const probeSheet = new CSSStyleSheet();
        probeSheet.replaceSync(`${PROBE_SELECTOR} {}`);
        probe = probeSheet.cssRules[0] as CSSStyleRule;
    } catch {
        return null;
    }

    scopeRules(sheet, probe);

    return Array.from(sheet.cssRules)
        .map((rule) => rule.cssText)
        .join("\n");
}

/**
 * Scopes every style rule of a sheet or grouping rule (`@media`, `@supports`, `@container`, `@layer`),
 * recursing into nested groups. Rules that cannot be scoped are dropped rather than left unscoped:
 *
 * - `@import` pulls in a whole foreign stylesheet whose rules this function never sees.
 * - A style rule whose rewritten selector list is not accepted by the parser would otherwise stay
 *   effective with its original, unscoped selector.
 *
 * At-rules without a selector list (`@keyframes`, `@font-face`, `@page`, `@property`) are left as they
 * are — they carry no element selector to scope (see the note on `LEGAL_DOCUMENT_SCOPE_CLASS`).
 * Selectors nested inside a style rule are relative to their already-scoped parent and need no
 * rewriting of their own.
 */
function scopeRules(parent: CSSStyleSheet | CSSGroupingRule, probe: CSSStyleRule): void {
    // Backwards, so removing a rule does not shift the indices still to be visited.
    for (let index = parent.cssRules.length - 1; index >= 0; index--) {
        const rule = parent.cssRules[index];
        if (rule instanceof CSSImportRule) {
            parent.deleteRule(index);
        } else if (rule instanceof CSSStyleRule) {
            if (!scopeStyleRule(rule, probe)) {
                parent.deleteRule(index);
            }
        } else if (rule instanceof CSSGroupingRule) {
            scopeRules(rule, probe);
        }
    }
}

/** Rewrites a style rule's selector list; returns `false` when the result was rejected by the parser. */
function scopeStyleRule(rule: CSSStyleRule, probe: CSSStyleRule): boolean {
    const scoped = splitSelectorList(rule.selectorText, probe).map(scopeSelector).join(", ");
    return scoped.length > 0 && trySetSelector(rule, scoped);
}

/**
 * Splits a serialized selector list into its single selectors. The split is validated by the parser
 * instead of by counting brackets: a fragment that is not a valid selector on its own (`:not(h1` from
 * `:not(h1, h2)`, `a[title="a` from `a[title="a,b"]`) is rejoined with the following fragment until the
 * parser accepts it.
 */
function splitSelectorList(selectorList: string, probe: CSSStyleRule): string[] {
    const selectors: string[] = [];
    let pending = "";

    for (const fragment of selectorList.split(",")) {
        pending = pending.length === 0 ? fragment : `${pending},${fragment}`;
        if (trySetSelector(probe, pending)) {
            selectors.push(pending.trim());
            pending = "";
        }
    }

    // A trailing remainder the parser never accepted is dropped — for a selector list coming from the
    // CSSOM this cannot happen, and dropping is the safe direction if it ever does.
    return selectors;
}

/**
 * Assigns `selector` to `rule` and reports whether the parser accepted it. Setting `selectorText` to an
 * invalid selector is a no-op per spec (it neither throws nor reports), so acceptance is detected by
 * writing a sentinel first and checking whether the assignment replaced it.
 */
function trySetSelector(rule: CSSStyleRule, selector: string): boolean {
    rule.selectorText = PROBE_SELECTOR;
    if (rule.selectorText !== PROBE_SELECTOR) {
        return false;
    }
    rule.selectorText = selector;
    return rule.selectorText !== PROBE_SELECTOR;
}

/**
 * Prefixes a single selector with the scope. Leading document-root selectors (`html`, `body`, `:root`,
 * also chained as `html > body`) are dropped first — the container takes the role of the document body
 * for the embedded content, so `body { ... }` styles the container itself. The prefix is applied
 * unconditionally; an already scoped selector simply gets a redundant but harmless second prefix.
 */
function scopeSelector(selector: string): string {
    const withoutRoot = stripRootSelectorHead(selector.trim());
    return withoutRoot.length === 0 ? SCOPE_PREFIX : `${SCOPE_PREFIX} ${withoutRoot}`;
}

/** Removes leading `html` / `body` / `:root` compounds, including a following combinator. */
function stripRootSelectorHead(selector: string): string {
    let rest = selector;
    for (let match = ROOT_SELECTOR_HEAD.exec(rest); match !== null; match = ROOT_SELECTOR_HEAD.exec(rest)) {
        rest = rest.slice(match[0].length).trim();
    }
    return rest;
}
